import {memo, useContext, useEffect, useState} from 'react'

import PropTypes from 'prop-types'

import Button from '@s-ui/react-atom-button'
import Context from '@s-ui/react-context'
import AnimationTransition, {TYPES} from '@s-ui/react-css-transition'
import MoleculeAutosuggestField from '@s-ui/react-molecule-autosuggest-field'
import MoleculeAutosuggestOption, {MoleculeDropdownOptionTextWrapStyles} from '@s-ui/react-molecule-dropdown-option'

const EMPTY_STRING = ''

const NO_OP = () => {}

const FormAutosuggest = ({
  hasButton = true,
  id,
  label = EMPTY_STRING,
  onChange = NO_OP,
  onSelect = NO_OP,
  onError = NO_OP,
  options = [],
  placeholder = EMPTY_STRING,
  rightIcon,
  value = EMPTY_STRING
}) => {
  const {domain, i18n} = useContext(Context)
  const [currentValue, setCurrentValue] = useState(value)
  const [inputErrorText, setInputErrorText] = useState(EMPTY_STRING)

  const config = domain.get('config')
  const noCodeRegex = config.get('REGEX_CODE')

  const filteredAutosuggestOptions = options.filter(option => {
    const lowerCaseOption = option.label.toLowerCase()
    const lowerCaseQuery = currentValue?.toLowerCase()
    return lowerCaseOption.includes(lowerCaseQuery)
  })

  const showButton = Boolean(hasButton && currentValue)
  const hasInputError = Boolean(inputErrorText)

  const _getSelectedOptionId = label => {
    const selectedOption = filteredAutosuggestOptions.find(option => option.label === label)
    return selectedOption?.id
  }
  const _handleOnChange = (_, {value}) => {
    const decodedString = decodeURIComponent(value)
    const hasInvalidChars = noCodeRegex.test(decodedString)

    if (hasInvalidChars) {
      onError()
      return setInputErrorText(i18n.t('COMMON.INVALID_FORMAT'))
    }
    setCurrentValue(value)
    setInputErrorText(EMPTY_STRING)
    if (!hasButton) onChange(_, {id: _getSelectedOptionId(value), value})
  }

  const _handleOnClick = _ => {
    onChange(_, {id: _getSelectedOptionId(currentValue), value: currentValue})
  }

  const _handleOnEnter = _ => {
    onChange(_, {
      id: _getSelectedOptionId(currentValue),
      value: currentValue
    })
  }

  const _handleOnSelect = (_, {value}) => {
    setCurrentValue(value)
    const action = hasButton ? onChange : onSelect
    action(_, {
      id: _getSelectedOptionId(value),
      value
    })
  }

  useEffect(() => {
    setCurrentValue(value)
  }, [value])

  return (
    <div className="mt-FormAutosuggest">
      <MoleculeAutosuggestField
        errorText={hasInputError && inputErrorText}
        id={id}
        label={label}
        onChange={_handleOnChange}
        onEnter={_handleOnEnter}
        onSelect={_handleOnSelect}
        placeholder={placeholder}
        rightIcon={rightIcon}
        value={currentValue}
      >
        {filteredAutosuggestOptions.map((option, i) => (
          <MoleculeAutosuggestOption
            key={i}
            value={option.label}
            textWrap={MoleculeDropdownOptionTextWrapStyles.LINE_WRAP}
          >
            {option.label}
          </MoleculeAutosuggestOption>
        ))}
      </MoleculeAutosuggestField>
      {!hasInputError && (
        <AnimationTransition type={TYPES.FADE_IN} in={showButton} unmountOnExit>
          <div className="mt-FormAutosuggest-button">
            <Button onClick={_handleOnClick}>{i18n.t('COMMON_FILTER')}</Button>
          </div>
        </AnimationTransition>
      )}
    </div>
  )
}

FormAutosuggest.displayName = 'FormAutosuggest'

FormAutosuggest.propTypes = {
  hasButton: PropTypes.bool,
  id: PropTypes.string,
  label: PropTypes.string,
  onChange: PropTypes.func,
  onError: PropTypes.func,
  onSelect: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string
    })
  ),
  placeholder: PropTypes.string,
  rightIcon: PropTypes.node,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
}

export default memo(FormAutosuggest)
